/**
 * SettingsModal — user preferences + admin controls.
 *
 * Sections:
 *   APPEARANCE (all users)
 *     - Theme presets
 *     - Custom accent color
 *   ADMIN (exec role only)
 *     - Change agency invite code
 *   TENANT BRANDING (exec role only, Phase 3 Part 2)
 *     - Display name, primary color, logo URL
 *     - Feature flags (terrain, nfc, incident, command, api)
 *
 * Theme picker → useThemeStore.setPreset / setCustomAccent (local, no sync).
 * Tenant branding → useTenantStore.updateConfig → PUT /api/v1/tenants/config.
 */
import { useState, useEffect } from 'react'
import { workerPutV1, workerGetV1, workerPostV1 } from '../../../api/worker'
import { useAuthStore, isExecRole } from '../../../stores/auth'
import { useThemeStore, PRESETS, type Theme } from '../../../stores/theme'
import { useTenantStore, type TenantFeatures } from '../../../stores/tenant'
import { modalOverlay, modalBox, darkInput } from './modalShared'
import ModalHeader from './ModalHeader'

const WORKER_URL = (import.meta.env.VITE_API_URL || 'https://castellan-security.com').replace(/\/$/, '')

interface SettingsModalProps {
  onClose: () => void
}

export default function SettingsModal({ onClose }: SettingsModalProps) {
  const session = useAuthStore(s => s.session)
  const canEditAdmin = isExecRole(session?.rank ?? '')

  const { theme, customAccent, setPreset, setCustomAccent } = useThemeStore()
  const { tenant, features, updateConfig } = useTenantStore()

  const [code, setCode] = useState('')
  const [msg, setMsg]   = useState('')
  const [accentInput, setAccentInput] = useState(customAccent || theme.accent)

  const [tenantName,    setTenantName]    = useState(tenant?.name ?? '')
  const [tenantPrimary, setTenantPrimary] = useState(tenant?.config.primary ?? '')
  const [tenantLogoUrl, setTenantLogoUrl] = useState(tenant?.config.logoUrl ?? '')
  const [tenantMsg,     setTenantMsg]     = useState('')
  const [tenantSaving,  setTenantSaving]  = useState(false)

  // Active sessions
  interface SessionInfo { id: string; device: string; createdAt: string; expiresAt: string; isCurrent: boolean }
  const [sessions, setSessions] = useState<SessionInfo[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)

  useEffect(() => {
    workerGetV1<{ success: boolean; sessions?: SessionInfo[] }>('/api/v1/auth/sessions')
      .then(res => { if (res.success && res.sessions) setSessions(res.sessions) })
      .catch(() => {})
      .finally(() => setSessionsLoading(false))
  }, [])

  async function revokeSession(id: string) {
    await workerPostV1('/api/v1/auth/sessions/revoke', { sessionId: id })
    setSessions(s => s.filter(x => x.id !== id))
  }

  async function saveInviteCode() {
    if (!code.trim()) return
    try {
      const res = await workerPutV1<{ success: boolean; error?: string }>(
        '/api/v1/admin/invite-code',
        { newCode: code.trim() },
      )
      setMsg(res.success ? '✅ Invite code updated!' : (res.error ?? 'Failed'))
    } catch {
      setMsg('Network error')
    }
  }

  function handleAccentSubmit() {
    if (/^#[0-9A-Fa-f]{6}$/.test(accentInput)) {
      setCustomAccent(accentInput)
    }
  }

  function resetAccent() {
    useThemeStore.setState({ customAccent: '' })
    setPreset(theme.id)
    setAccentInput(PRESETS.find(p => p.id === theme.id)?.accent ?? theme.accent)
    try {
      localStorage.setItem('castellan_theme', JSON.stringify({ id: theme.id, customAccent: null }))
    } catch { /* silent */ }
  }

  async function saveTenantBranding() {
    setTenantSaving(true)
    setTenantMsg('')
    try {
      const patch: { primary?: string; logoUrl?: string } = {}
      if (tenantPrimary) patch.primary = tenantPrimary
      if (tenantLogoUrl !== (tenant?.config.logoUrl ?? '')) patch.logoUrl = tenantLogoUrl
      const ok = await updateConfig(patch, tenantName || undefined)
      setTenantMsg(ok ? '✅ Tenant branding saved' : 'Failed to save tenant branding')
    } catch {
      setTenantMsg('Network error')
    } finally {
      setTenantSaving(false)
    }
  }

  async function toggleFeature(name: keyof TenantFeatures) {
    if (!tenant) return
    const mergedFeatures = { ...features, [name]: !features[name] }
    await updateConfig({ features: mergedFeatures })
  }

  const [billingLoading, setBillingLoading] = useState(false)
  const [billingMsg,     setBillingMsg]     = useState('')

  async function openBillingPortal() {
    setBillingLoading(true)
    setBillingMsg('')
    try {
      const res = await fetch(`${WORKER_URL}/api/v1/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.token ?? ''}`,
        },
      })
      const data = await res.json()
      if (data.success && data.url) {
        window.open(data.url, '_blank')
      } else {
        setBillingMsg(data.error || 'Could not open billing portal')
      }
    } catch {
      setBillingMsg('Network error')
    } finally {
      setBillingLoading(false)
    }
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 10,
    color: 'var(--text-muted)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  }

  const sectionStyle: React.CSSProperties = {
    marginBottom: 20,
    paddingBottom: 20,
    borderBottom: '1px solid var(--border)',
  }

  return (
    <div
      style={modalOverlay}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={modalBox(480)}>
        <ModalHeader title="⚙️ Settings" onClose={onClose} />

        {/* BILLING */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Subscription & Billing</label>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 10 }}>
            Manage your subscription, update payment method, or cancel.
          </div>
          <button
            onClick={openBillingPortal}
            disabled={billingLoading}
            style={{
              background: 'linear-gradient(135deg,#c9a84c,#d97706)',
              border: 'none', borderRadius: 8,
              padding: '10px 20px',
              color: '#0a1628', fontWeight: 700,
              fontSize: 13, cursor: billingLoading ? 'wait' : 'pointer',
              opacity: billingLoading ? 0.7 : 1,
            }}
          >
            {billingLoading ? 'Opening…' : '💳 Manage Billing'}
          </button>
          {billingMsg && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#f87171' }}>{billingMsg}</div>
          )}
        </div>

        {/* APPEARANCE */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Theme</label>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 10,
            marginBottom: 14,
          }}>
            {PRESETS.map(p => <ThemeSwatch key={p.id} preset={p} active={theme.id === p.id} onClick={() => setPreset(p.id)} />)}
          </div>

          <label style={labelStyle}>Custom Accent Color</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              value={/^#[0-9A-Fa-f]{6}$/.test(accentInput) ? accentInput : '#c9a84c'}
              onChange={e => {
                setAccentInput(e.target.value)
                setCustomAccent(e.target.value)
              }}
              style={{
                width: 44,
                height: 36,
                border: '1px solid var(--border)',
                borderRadius: 8,
                cursor: 'pointer',
                background: 'var(--bg-elevated)',
                padding: 2,
              }}
            />
            <input
              type="text"
              value={accentInput}
              onChange={e => setAccentInput(e.target.value)}
              onBlur={handleAccentSubmit}
              onKeyDown={e => e.key === 'Enter' && handleAccentSubmit()}
              placeholder="#c9a84c"
              style={{ ...darkInput, flex: 1, fontFamily: 'monospace' }}
            />
            <button
              onClick={resetAccent}
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '8px 14px',
                color: 'var(--text-muted)',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Reset
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 6 }}>
            Picks a custom highlight color on top of the current theme. Reset restores the preset's default accent.
          </div>
        </div>

        {/* ACTIVE SESSIONS */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Active Sessions</label>
          {sessionsLoading ? (
            <div style={{ color: 'var(--text-hint)', fontSize: 12 }}>Loading…</div>
          ) : sessions.length === 0 ? (
            <div style={{ color: 'var(--text-hint)', fontSize: 12 }}>No active sessions found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sessions.map(s => (
                <div key={s.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'var(--bg-deep)', border: `1px solid ${s.isCurrent ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 8, padding: '8px 10px',
                }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: s.isCurrent ? 'var(--accent)' : 'var(--text-primary)' }}>
                      {s.device || 'Unknown device'} {s.isCurrent && '← current'}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 2 }}>
                      Since {new Date(s.createdAt).toLocaleString()}
                    </div>
                  </div>
                  {!s.isCurrent && (
                    <button onClick={() => revokeSession(s.id)} style={{
                      background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: 6, color: '#ef4444', fontSize: 11, padding: '4px 10px',
                      cursor: 'pointer', fontWeight: 600,
                    }}>Revoke</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ADMIN — invite code (gated) */}
        {canEditAdmin && (
          <div style={sectionStyle}>
            <label style={labelStyle}>Change Invite Code</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="New invite code…"
                style={{ ...darkInput, flex: 1 }}
              />
              <button
                onClick={saveInviteCode}
                style={{
                  background: 'var(--accent)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  color: 'var(--bg-deep)',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                Save
              </button>
            </div>
            {msg && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: msg.startsWith('✅') ? '#22c55e' : '#f87171',
                }}
              >
                {msg}
              </div>
            )}
          </div>
        )}

        {/* TENANT BRANDING — exec only (Phase 3 Part 2) */}
        {canEditAdmin && tenant && (
          <div style={sectionStyle}>
            <label style={labelStyle}>Tenant Branding</label>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 10 }}>
              Tier: <strong style={{ color: 'var(--accent)' }}>{tenant.tier}</strong>
              {' • '}
              ID: <code style={{ background: 'var(--bg-elevated)', padding: '1px 6px', borderRadius: 4 }}>{tenant.id}</code>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                type="text"
                value={tenantName}
                onChange={e => setTenantName(e.target.value)}
                placeholder="Agency display name"
                style={darkInput}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="color"
                  value={/^#[0-9A-Fa-f]{6}$/.test(tenantPrimary) ? tenantPrimary : '#c9a84c'}
                  onChange={e => setTenantPrimary(e.target.value)}
                  style={{
                    width: 44,
                    height: 36,
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: 'var(--bg-elevated)',
                    padding: 2,
                  }}
                />
                <input
                  type="text"
                  value={tenantPrimary}
                  onChange={e => setTenantPrimary(e.target.value)}
                  placeholder="Primary color #hex"
                  style={{ ...darkInput, flex: 1, fontFamily: 'monospace' }}
                />
              </div>
              <input
                type="text"
                value={tenantLogoUrl}
                onChange={e => setTenantLogoUrl(e.target.value)}
                placeholder="Logo URL (optional)"
                style={darkInput}
              />
              <button
                onClick={saveTenantBranding}
                disabled={tenantSaving}
                style={{
                  background: 'var(--accent)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 16px',
                  color: 'var(--bg-deep)',
                  fontWeight: 700,
                  cursor: tenantSaving ? 'wait' : 'pointer',
                  fontSize: 13,
                  opacity: tenantSaving ? 0.6 : 1,
                }}
              >
                {tenantSaving ? 'Saving…' : 'Save Tenant Branding'}
              </button>
            </div>
            {tenantMsg && (
              <div style={{
                marginTop: 8,
                fontSize: 13,
                color: tenantMsg.startsWith('✅') ? '#22c55e' : '#f87171',
              }}>
                {tenantMsg}
              </div>
            )}

            <label style={{ ...labelStyle, marginTop: 16 }}>Feature Flags</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {(['terrain', 'nfc', 'incident', 'command', 'api'] as const).map(f => (
                <label key={f} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 12,
                  color: 'var(--text-primary)',
                }}>
                  <input
                    type="checkbox"
                    checked={features[f]}
                    onChange={() => toggleFeature(f)}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  <span style={{ textTransform: 'capitalize' }}>{f}</span>
                </label>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 6 }}>
              Tier defaults apply automatically; toggles here override per tenant.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface ThemeSwatchProps {
  preset: Theme
  active: boolean
  onClick: () => void
}

function ThemeSwatch({ preset, active, onClick }: ThemeSwatchProps) {
  return (
    <button
      onClick={onClick}
      title={preset.label}
      style={{
        padding: 0,
        border: active ? `2px solid ${preset.accent}` : '1px solid var(--border)',
        borderRadius: 10,
        cursor: 'pointer',
        background: 'transparent',
        overflow: 'hidden',
        position: 'relative',
        transition: 'transform 0.15s, border-color 0.15s',
        transform: active ? 'scale(1.03)' : 'scale(1)',
      }}
    >
      <div style={{
        height: 56,
        background: preset.bgDeep,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}>
        <div style={{
          height: 18,
          background: preset.bgSurface,
          borderBottom: `1px solid ${preset.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingLeft: 6,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: preset.accent }} />
        </div>
        <div style={{
          padding: '4px 6px',
          display: 'flex',
          gap: 3,
        }}>
          <div style={{ width: '32%', height: 4, borderRadius: 2, background: preset.textPrimary, opacity: 0.8 }} />
          <div style={{ width: '22%', height: 4, borderRadius: 2, background: preset.textMuted, opacity: 0.6 }} />
          <div style={{ width: '14%', height: 4, borderRadius: 2, background: preset.accent }} />
        </div>
      </div>
      <div style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '5px 4px',
        color: active ? preset.accent : 'var(--text-muted)',
        background: 'var(--bg-surface)',
        textAlign: 'center',
      }}>
        {preset.label}
      </div>
      {active && (
        <div style={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: preset.accent,
          color: preset.bgDeep,
          fontSize: 10,
          fontWeight: 900,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>✓</div>
      )}
    </button>
  )
}
